import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PLANBASE_CONFIG } from '../config';
import { MobilePageHeader } from '../components/MobilePageHeader';
import { useHideBottomNav } from '../hooks/useHideBottomNav';
import { ArrowPathIcon, ArrowTopRightOnSquareIcon, CheckIcon, ClipboardIcon, XMarkIcon } from '@heroicons/react/24/outline';

export default function PortfolioSetup() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  useHideBottomNav();
  const [loading, setLoading] = useState(false);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [portfolioUrl, setPortfolioUrl] = useState('');
  const [copied, setCopied] = useState(false);

  const [formData, setFormData] = useState({
    username: user?.metadata?.username || '',
    full_name: user?.metadata?.full_name || user?.name || '',
    bio: user?.metadata?.bio || '',
    location: user?.metadata?.location || '',
    website: user?.metadata?.website || '',
    avatar_url: user?.avatar_url || user?.metadata?.avatar_url || '',
  });

  useEffect(() => {
    if (user?.metadata?.username) {
      setPortfolioUrl(`${window.location.origin}/portfolio/${user.metadata.username}`);
    }
  }, [user]);

  useEffect(() => {
    if (!formData.username || formData.username === user?.metadata?.username) {
      setUsernameAvailable(null); return;
    }
    const timer = setTimeout(() => checkUsernameAvailability(formData.username), 500);
    return () => clearTimeout(timer);
  }, [formData.username]);

  const checkUsernameAvailability = async (username: string) => {
    if (!username || username.length < 3) { setUsernameAvailable(null); return; }
    setCheckingUsername(true);
    try {
      const res = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/planbase/check-username?username=${username}`, {
        headers: { 'Content-Type': 'application/json', 'X-Project-ID': PLANBASE_CONFIG.projectId, 'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}` },
      });
      const data = await res.json();
      setUsernameAvailable((data.data || data).available);
    } catch { setUsernameAvailable(null); }
    finally { setCheckingUsername(false); }
  };

  const validateUsername = (u: string) => {
    if (u.length < 3) return 'At least 3 characters';
    if (u.length > 30) return 'Max 30 characters';
    if (!/^[a-z0-9-]+$/.test(u)) return 'Lowercase letters, numbers, hyphens only';
    if (u.startsWith('-') || u.endsWith('-')) return 'Cannot start or end with a hyphen';
    if (u.includes('--')) return 'No consecutive hyphens';
    return null;
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') });
    setUsernameAvailable(null);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setMessage(null);
    try {
      const err = validateUsername(formData.username);
      if (err) { setMessage({ type: 'error', text: err }); return; }
      if (formData.username !== user?.metadata?.username) {
        if (usernameAvailable === false) { setMessage({ type: 'error', text: 'Username is not available' }); return; }
        const res = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/planbase/check-username?username=${formData.username}`, {
          headers: { 'Content-Type': 'application/json', 'X-Project-ID': PLANBASE_CONFIG.projectId, 'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}` },
        });
        const d = await res.json();
        if (!(d.data || d).available) { setMessage({ type: 'error', text: 'Username is not available' }); setUsernameAvailable(false); return; }
      }
      const response = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/planbase/members/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-Project-ID': PLANBASE_CONFIG.projectId, 'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`, 'X-User-Token': localStorage.getItem('planbase_token') || '' },
        body: JSON.stringify({ metadata: { ...user?.metadata, ...formData } }),
      });
      if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.error || 'Failed'); }
      const updatedUser = { ...user, avatar_url: formData.avatar_url, metadata: { ...user?.metadata, ...formData } };
      localStorage.setItem('planbase_user', JSON.stringify(updatedUser));
      refreshUser();
      const url = `${window.location.origin}/portfolio/${formData.username}`;
      setPortfolioUrl(url);
      setMessage({ type: 'success', text: 'Portfolio saved successfully!' });
      setTimeout(() => navigate(`/portfolio/${formData.username}`), 1500);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to save portfolio' });
    } finally { setLoading(false); }
  };

  const copyToClipboard = () => { navigator.clipboard.writeText(portfolioUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const usernameStatus = (() => {
    if (!formData.username || formData.username === user?.metadata?.username) return null;
    const err = validateUsername(formData.username);
    if (err) return { type: 'error', text: err };
    if (checkingUsername) return { type: 'checking', text: 'Checking...' };
    if (usernameAvailable === true) return { type: 'success', text: 'Available' };
    if (usernameAvailable === false) return { type: 'error', text: 'Already taken' };
    return null;
  })();

  // Portfolio setup is available to every signed-in user. Previously
  // this was a hard paywall; the paid gates for Planbase live further
  // in (uploading more than the free project cap, PDF export beyond
  // the free credit, etc.), not on the public-profile basics.

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <MobilePageHeader title="Portfolio Setup" onBack={() => navigate('/profile')} />
      {/* Desktop header */}
      <div className="hidden md:flex items-center justify-between">
        <h1 className="text-sm font-semibold text-foreground uppercase tracking-wide">Portfolio Setup</h1>
        <Button onClick={() => navigate('/profile')} variant="outline" size="sm">Cancel</Button>
      </div>

      {/* Live URL banner */}
      {portfolioUrl && user?.metadata?.username && (
        <div className="flex items-center justify-between bg-card border border-border rounded-2xl px-4 py-3 gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-2 w-2 rounded-full bg-green-500 flex-shrink-0" />
            <span className="text-xs text-muted-foreground truncate">{portfolioUrl}</span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={copyToClipboard} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
              {copied ? <CheckIcon className="h-3.5 w-3.5 text-green-500" /> : <ClipboardIcon className="h-3.5 w-3.5 text-muted-foreground" />}
            </button>
            <button onClick={() => window.open(portfolioUrl, '_blank')} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          </div>
        </div>
      )}

      {/* Form Card */}
      <form onSubmit={handleSubmit}>
        <div className="bg-card border border-border rounded-2xl overflow-hidden">

          {/* Avatar + Name row */}
          <div className="p-5 flex items-center gap-4 border-b border-border">
            {formData.avatar_url ? (
              <img src={formData.avatar_url} alt="Avatar" className="h-14 w-14 rounded-full object-cover flex-shrink-0 border border-border" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            ) : (
              <div className="h-14 w-14 rounded-full bg-muted flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <label className="text-xs text-muted-foreground">Avatar URL</label>
              <Input type="url" name="avatar_url" value={formData.avatar_url} onChange={handleChange} placeholder="https://example.com/avatar.jpg" className="mt-1" />
            </div>
          </div>

          {/* Fields */}
          <div className="divide-y divide-border">
            {/* Username */}
            <div className="px-5 py-4">
              <label className="text-xs text-muted-foreground block mb-1.5">Username *</label>
              <div className="flex items-center gap-0">
                <span className="h-8 inline-flex items-center px-3 border border-r-0 border-input bg-muted text-muted-foreground text-xs rounded-l-md whitespace-nowrap flex-shrink-0">
                  /portfolio/
                </span>
                <div className="relative flex-1">
                  <Input type="text" name="username" value={formData.username} onChange={handleUsernameChange} placeholder="your-username" required className="rounded-l-none pr-8" />
                  <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
                    {checkingUsername && <ArrowPathIcon className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                    {!checkingUsername && usernameStatus?.type === 'success' && <CheckIcon className="h-3.5 w-3.5 text-green-500" />}
                    {!checkingUsername && usernameStatus?.type === 'error' && <XMarkIcon className="h-3.5 w-3.5 text-destructive" />}
                  </div>
                </div>
              </div>
              {usernameStatus && (
                <p className={`text-xs mt-1 ${usernameStatus.type === 'success' ? 'text-green-600' : usernameStatus.type === 'error' ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {usernameStatus.text}
                </p>
              )}
            </div>

            {/* Full Name */}
            <div className="px-5 py-4">
              <label className="text-xs text-muted-foreground block mb-1.5">Full Name *</label>
              <Input type="text" name="full_name" value={formData.full_name} onChange={handleChange} placeholder="Your full name" required />
            </div>

            {/* Bio */}
            <div className="px-5 py-4">
              <label className="text-xs text-muted-foreground block mb-1.5">Bio</label>
              <textarea
                name="bio" value={formData.bio} onChange={handleChange}
                placeholder="Tell visitors about yourself..."
                rows={3} maxLength={500}
                className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-primary bg-background text-foreground text-sm resize-y min-h-[80px] max-h-[240px] scrollbar-hide"
              />
              <p className="text-xs text-muted-foreground mt-1">{formData.bio.length}/500</p>
            </div>

            {/* Location + Website */}
            <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground block mb-1.5">Location</label>
                <Input type="text" name="location" value={formData.location} onChange={handleChange} placeholder="City, Country" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1.5">Website</label>
                <Input type="url" name="website" value={formData.website} onChange={handleChange} placeholder="https://yoursite.com" />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-5 py-4 bg-muted/40 border-t border-border flex items-center justify-between gap-3">
            {message && (
              <p className={`text-xs ${message.type === 'success' ? 'text-green-600' : 'text-destructive'}`}>{message.text}</p>
            )}
            <div className="flex gap-2 ml-auto">
              <Button type="button" variant="outline" size="sm" onClick={() => navigate('/profile')}>Cancel</Button>
              <Button
                type="submit" size="sm"
                disabled={loading || checkingUsername || (usernameAvailable === false && formData.username !== user?.metadata?.username)}
                className="bg-primary hover:bg-primary/90 text-white"
              >
                {loading ? <><ArrowPathIcon className="mr-1.5 h-3.5 w-3.5 animate-spin" />Saving...</> : user?.metadata?.username ? 'Update' : 'Create Portfolio'}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
